import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectFlatList,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@repo/components/src/ui/select";
import { AlertCircleIcon, ChevronDown } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps, FormOptions } from "../types";

/**
 * The text the trigger shows for the current value. Gluestack keeps it in its
 * own state, set only when an option is picked, so a select mounted with a
 * value (a prefilled form, a remounted step) would show the raw value instead.
 */
const getSelectedLabel = (
  options: FormOptions[],
  value: unknown,
  initialLabel: string | undefined,
): string =>
  options.find((option) => String(option.value) === String(value))?.label ??
  initialLabel ??
  "";

export default function FormInputSelect<TFieldValues extends FieldValues>(
  props: FormInputSelectProps<TFieldValues> & {
    initialLabel?: string;
    triggerProps?: ComponentProps<typeof SelectTrigger>;
  },
) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    placeholder,
    initialLabel,
    triggerProps,
  } = props;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
          ref={focusScrollRef(ref)}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <Select
            initialLabel={initialLabel}
            selectedValue={value}
            onValueChange={onChange}
          >
            <SelectTrigger variant="outline" size={size} {...triggerProps}>
              <SelectInput
                value={getSelectedLabel(options, value, initialLabel)}
                placeholder={placeholder}
                className="flex-1 shrink font-body"
              />
              <SelectIcon as={ChevronDown} className="mr-3" />
            </SelectTrigger>
            <SelectPortal>
              <SelectBackdrop />
              <SelectContent className="pb-safe-offset-4 max-h-[85%]">
                <SelectDragIndicatorWrapper>
                  <SelectDragIndicator />
                </SelectDragIndicatorWrapper>
                <SelectFlatList
                  className="mt-2"
                  data={options}
                  renderItem={({ item }) => (
                    <SelectItem
                      label={(item as FormOptions).label}
                      value={(item as FormOptions).value as string}
                      key={(item as FormOptions).value}
                      isDisabled={(item as FormOptions).isDisabled}
                    />
                  )}
                />
              </SelectContent>
            </SelectPortal>
          </Select>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
